import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { DisplayRef } from '@repo/sdk/models/components';

import { EnhancedReading } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { LatestValue } from '@/types/reading-types';
import { getLatestPrimitiveValue } from '@/types/reading-types';

import { equipmentResponseAtom } from './equipment-response.state';

export const buildEnhancedReading = (metric: DisplayRef, latestValue: LatestValue) => {
  return {
    ...metric,
    key: metric?.ref?.id,
    value: getLatestPrimitiveValue(latestValue),
    units: metric?.ref?.units,
    valueKind: latestValue?.kind,
    rawValue: latestValue,
  };
};

/**
 * displayMetricsListAtom
 * maintains the list of enhanced metrics for the display metrics
 *
 * Builds the list of enhanced metrics for the display metrics
 * by mapping the display metrics to the enhanced metrics
 *
 * Possible future optimizations: convert this to a splitAtom to avoid re-rendering the entire list when a single metric changes
 */
export const displayMetricsListAtom = atom<EnhancedReading[]>((get) => {
  const equipment = get(equipmentResponseAtom);

  return (
    equipment?.displayMetrics?.map((metric) => {
      const latestValue = get(readingsFamily({ pointId: metric?.ref?.id } as LatestValue));

      return buildEnhancedReading(metric, latestValue);
    }) ?? []
  );
});
displayMetricsListAtom.debugLabel = 'displayMetricsListAtom';

/**
 * getDisplayMetricAtom
 * returns an atom for retrieving a specific metric from the display metrics list
 *
 * @param id - The unique identifier of the metric to retrieve
 * @returns A select atom that returns the matching enhanced metric and only re-renders when
 *          the metric's value or label changes
 */
export const getDisplayMetricAtom = (id: string) => {
  const metricAtom = atom((get) => {
    const metrics = get(displayMetricsListAtom);

    return metrics?.find((metric) => metric?.ref?.id === id);
  });

  return selectAtom(
    metricAtom,
    (atom) => atom,
    (a, b) => {
      return a?.value === b?.value && a?.label === b?.label;
    }
  );
};
getDisplayMetricAtom.debugLabel = 'getDisplayMetricAtom';
